import { Node, mergeAttributes } from '@tiptap/core';

export interface BlogTeaserBoxOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blogTeaserBox: {
      setBlogTeaserBox: (options: {
        title: string;
        intro: string;
        imageUrl?: string;
        buttonText: string;
        buttonUrl: string;
        accentColor?: string;
        backgroundColor?: string;
      }) => ReturnType;
      updateBlogTeaserBox: (options: {
        title?: string;
        intro?: string;
        imageUrl?: string;
        buttonText?: string;
        buttonUrl?: string;
        accentColor?: string;
        backgroundColor?: string;
      }) => ReturnType;
    };
  }
}

export const BlogTeaserBox = Node.create<BlogTeaserBoxOptions>({
  name: 'blogTeaserBox',

  group: 'block',

  atom: true,

  selectable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const nodeRef = { current: node };

      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-blog-teaser-wrapper', 'true');
      wrapper.setAttribute('contenteditable', 'false');
      wrapper.style.cssText = `
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        padding: 1rem;
        background-color: ${node.attrs.backgroundColor || '#f9fafb'};
        margin: 1.5rem 0;
        cursor: pointer;
        position: relative;
        transition: all 0.2s;
      `;
      wrapper.className = 'blog-teaser-wrapper-node';

      const editIndicator = document.createElement('div');
      editIndicator.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
          <path d="m15 5 4 4"/>
        </svg>
      `;
      editIndicator.style.cssText = `
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        background-color: #FF6B35;
        color: white;
        width: 28px;
        height: 28px;
        border-radius: 0.375rem;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.7;
        transition: opacity 0.2s;
        cursor: pointer;
        z-index: 10;
      `;
      wrapper.appendChild(editIndicator);

      wrapper.addEventListener('mouseenter', () => {
        editIndicator.style.opacity = '1';
      });

      wrapper.addEventListener('mouseleave', () => {
        editIndicator.style.opacity = '0.7';
      });

      const content = document.createElement('div');

      if (node.attrs.imageUrl) {
        const img = document.createElement('img');
        img.src = node.attrs.imageUrl;
        img.alt = node.attrs.title;
        img.style.cssText = 'width: 100%; height: auto; border-radius: 0.5rem; margin-bottom: 1rem;';
        content.appendChild(img);
      }

      const h3 = document.createElement('h3');
      h3.textContent = node.attrs.title;
      h3.style.cssText = 'font-size: 1.25rem; font-weight: 600; color: #1f2937; margin-bottom: 0.5rem;';
      content.appendChild(h3);

      const intro = document.createElement('p');
      intro.textContent = node.attrs.intro;
      intro.style.cssText = 'color: #4b5563; margin-bottom: 1rem;';
      content.appendChild(intro);

      const buttonWrapper = document.createElement('p');
      buttonWrapper.style.cssText = 'text-align: left; margin: 0;';

      const button = document.createElement('a');
      button.href = node.attrs.buttonUrl || '#';
      button.textContent = node.attrs.buttonText || 'Lees meer';
      button.setAttribute('data-teaser-button', 'true');
      button.style.cssText = `
        display: inline-block;
        background-color: ${node.attrs.accentColor || '#FF6B35'};
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 0.375rem;
        text-decoration: none;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      `;

      button.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
      });

      buttonWrapper.appendChild(button);
      content.appendChild(buttonWrapper);

      wrapper.appendChild(content);

      const handleClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
          }
        }
      };

      const handleEdit = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const event = new CustomEvent('blogTeaserBoxEdit', {
          detail: {
            title: nodeRef.current.attrs.title,
            intro: nodeRef.current.attrs.intro,
            imageUrl: nodeRef.current.attrs.imageUrl,
            buttonText: nodeRef.current.attrs.buttonText,
            buttonUrl: nodeRef.current.attrs.buttonUrl,
            accentColor: nodeRef.current.attrs.accentColor,
            backgroundColor: nodeRef.current.attrs.backgroundColor,
          },
        });
        document.dispatchEvent(event);
      };

      const updateSelectionState = () => {
        const { selection } = editor.state;
        const pos = getPos();
        const isSelected = typeof pos === 'number' && selection.from === pos;

        if (isSelected) {
          wrapper.style.outline = '3px solid #FF6B35';
          wrapper.style.outlineOffset = '2px';
          wrapper.style.backgroundColor = 'rgba(255, 107, 53, 0.05)';
        } else {
          wrapper.style.outline = 'none';
          wrapper.style.backgroundColor = nodeRef.current.attrs.backgroundColor || '#f9fafb';
        }
      };

      editIndicator.addEventListener('click', handleEdit);
      wrapper.addEventListener('click', handleClick);
      editor.on('selectionUpdate', updateSelectionState);
      editor.on('transaction', updateSelectionState);

      updateSelectionState();

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'blogTeaserBox') return false;

          nodeRef.current = updatedNode;

          content.innerHTML = '';

          if (updatedNode.attrs.imageUrl) {
            const img = document.createElement('img');
            img.src = updatedNode.attrs.imageUrl;
            img.alt = updatedNode.attrs.title;
            img.style.cssText = 'width: 100%; height: auto; border-radius: 0.5rem; margin-bottom: 1rem;';
            content.appendChild(img);
          }

          const h3Updated = document.createElement('h3');
          h3Updated.textContent = updatedNode.attrs.title;
          h3Updated.style.cssText = 'font-size: 1.25rem; font-weight: 600; color: #1f2937; margin-bottom: 0.5rem;';
          content.appendChild(h3Updated);

          const introUpdated = document.createElement('p');
          introUpdated.textContent = updatedNode.attrs.intro;
          introUpdated.style.cssText = 'color: #4b5563; margin-bottom: 1rem;';
          content.appendChild(introUpdated);

          const buttonWrapperUpdated = document.createElement('p');
          buttonWrapperUpdated.style.cssText = 'text-align: left; margin: 0;';

          const buttonUpdated = document.createElement('a');
          buttonUpdated.href = updatedNode.attrs.buttonUrl || '#';
          buttonUpdated.textContent = updatedNode.attrs.buttonText || 'Lees meer';
          buttonUpdated.setAttribute('data-teaser-button', 'true');
          buttonUpdated.style.cssText = `
            display: inline-block;
            background-color: ${updatedNode.attrs.accentColor || '#FF6B35'};
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.375rem;
            text-decoration: none;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          `;

          buttonUpdated.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();
          });

          buttonWrapperUpdated.appendChild(buttonUpdated);
          content.appendChild(buttonWrapperUpdated);

          wrapper.style.backgroundColor = updatedNode.attrs.backgroundColor || '#f9fafb';

          return true;
        },
        destroy: () => {
          editor.off('selectionUpdate', updateSelectionState);
          editor.off('transaction', updateSelectionState);
          editIndicator.removeEventListener('click', handleEdit);
          wrapper.removeEventListener('click', handleClick);
        },
      };
    };
  },

  addAttributes() {
    return {
      title: {
        default: '',
        parseHTML: (element) => {
          const h3 = element.querySelector('h3');
          return h3?.textContent?.trim() || '';
        },
      },
      intro: {
        default: '',
        parseHTML: (element) => {
          const p = element.querySelector('p:not([style*="text-align"])');
          return p?.textContent?.trim() || '';
        },
      },
      imageUrl: {
        default: null,
        parseHTML: (element) => {
          const img = element.querySelector('img');
          return img?.getAttribute('src') || null;
        },
      },
      buttonText: {
        default: 'Lees meer',
        parseHTML: (element) => {
          const button = element.querySelector('a[data-teaser-button]');
          return button?.textContent?.trim() || 'Lees meer';
        },
      },
      buttonUrl: {
        default: '#',
        parseHTML: (element) => {
          const button = element.querySelector('a[data-teaser-button]');
          return button?.getAttribute('href') || '#';
        },
      },
      accentColor: {
        default: '#FF6B35',
        parseHTML: (element) => {
          const button = element.querySelector('a[data-teaser-button]');
          if (button) {
            const style = button.getAttribute('style') || '';
            const match = style.match(/background-color:\s*([^;]+)/);
            return match ? match[1].trim() : '#FF6B35';
          }
          return '#FF6B35';
        },
      },
      backgroundColor: {
        default: '#f9fafb',
        parseHTML: (element) => {
          const style = element.getAttribute('style') || '';
          const match = style.match(/background-color:\s*([^;]+)/);
          return match ? match[1].trim() : '#f9fafb';
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-blog-teaser-wrapper]',
        priority: 100,
      },
    ];
  },

  renderHTML({ node }) {
    const { title, intro, imageUrl, buttonText, buttonUrl, accentColor, backgroundColor } = node.attrs;

    const imageHtml = imageUrl
      ? [
          'img',
          {
            src: imageUrl,
            alt: title,
            style: 'width: 100%; height: auto; border-radius: 0.5rem; margin-bottom: 1rem;',
          },
        ]
      : null;

    const children = [
      imageHtml,
      [
        'h3',
        { style: 'font-size: 1.25rem; font-weight: 600; color: #1f2937; margin-bottom: 0.5rem;' },
        title,
      ],
      [
        'p',
        { style: 'color: #4b5563; margin-bottom: 1rem;' },
        intro,
      ],
      [
        'p',
        { style: 'text-align: left; margin: 0;' },
        [
          'a',
          {
            href: buttonUrl || '#',
            'data-teaser-button': 'true',
            style: `display: inline-block; background-color: ${accentColor || '#FF6B35'}; color: white; padding: 0.5rem 1rem; border-radius: 0.375rem; text-decoration: none; font-weight: 500;`,
          },
          buttonText || 'Lees meer',
        ],
      ],
    ].filter(Boolean);

    return [
      'div',
      mergeAttributes(
        {
          'data-blog-teaser-wrapper': 'true',
          style: `border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 1rem; background-color: ${backgroundColor || '#f9fafb'}; margin: 1.5rem 0;`,
        },
        this.options.HTMLAttributes
      ),
      ...children,
    ];
  },

  addCommands() {
    return {
      setBlogTeaserBox:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: options.title,
              intro: options.intro,
              imageUrl: options.imageUrl || null,
              buttonText: options.buttonText,
              buttonUrl: options.buttonUrl,
              accentColor: options.accentColor || '#FF6B35',
              backgroundColor: options.backgroundColor || '#f9fafb',
            },
          });
        },

      updateBlogTeaserBox:
        (options) =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const node = state.doc.nodeAt(selection.from);

          if (!node || node.type.name !== this.name) {
            return false;
          }

          if (dispatch) {
            const attrs = { ...node.attrs, ...options };
            tr.setNodeMarkup(selection.from, undefined, attrs);
            dispatch(tr);
          }

          return true;
        },
    };
  },
});
